import { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Custom error handler for queries
const queryErrorHandler = (error: unknown) => {
  const message = error instanceof Error ? error.message : 'An error occurred';
  toast.error(message);
};

// Custom success handler for mutations
const mutationSuccessHandler = (message: string) => {
  toast.success(message);
};

// Default query options
export const defaultQueryOptions = {
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 10, // 10 minutes
  retry: (failureCount: number, error: unknown) => {
    // Don't retry on 401 or 403 errors
    if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
      return false;
    }
    // Retry up to 3 times for other errors
    return failureCount < 3;
  },
  onError: queryErrorHandler,
};

// Default mutation options
export const defaultMutationOptions = {
  onError: queryErrorHandler,
  onSuccess: (data: any, variables: any, context: any) => {
    if (data?.message) {
      mutationSuccessHandler(data.message);
    }
  },
};

// Create a new query client with enhanced configuration
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: defaultQueryOptions,
      mutations: defaultMutationOptions,
    },
  });
};

// Helper function for optimistic updates
export const optimisticUpdate = <T>(
  queryClient: QueryClient,
  queryKey: any[],
  updater: (oldData: T) => T
) => {
  queryClient.setQueryData(queryKey, updater);
};

// Helper function for cache invalidation
export const invalidateQueries = (
  queryClient: QueryClient,
  queryKey: any[],
  options = { exact: false }
) => {
  queryClient.invalidateQueries({ queryKey, ...options });
};

// Helper function for handling paginated queries
export const getNextPageParam = (lastPage: any) => {
  return lastPage.nextCursor;
};

// Helper function for handling infinite queries
export const getInfiniteQueryConfig = (queryKey: any[]) => ({
  getNextPageParam,
  queryKey,
  ...defaultQueryOptions,
}); 